import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { getInitials } from '@/utils/helpers';
import { STAFF_ROLE_LABELS } from '@/types/auth.types';
import { ConfirmDialog } from '@/components/ui';
import { useCommandPalette } from '@/components/command-palette';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { TourLauncher } from '@/tour';
import { spring } from '@/lib/motion';
import type { StaffRole } from '@/types';

interface HeaderProps {
  onMenuClick: () => void;
}

const isMac =
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform);

export function Header({ onMenuClick }: HeaderProps) {
  const { user, signOut } = useAuth();
  const { open: openPalette } = useCommandPalette();
  const navigate = useNavigate();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Cierre al hacer click fuera + Escape (mismo patrón que NotificationBell)
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(target)
      ) {
        setMenuOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [menuOpen]);

  const userName = user ? `${user.firstName} ${user.lastName}` : 'Usuario';
  const userRole = user?.profiles?.staff?.role as StaffRole | undefined;
  const roleLabel = userRole ? STAFF_ROLE_LABELS[userRole] : '';

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-background/85 px-4 backdrop-blur-md supports-[backdrop-filter]:bg-background/70 lg:px-6">
      <div className="flex items-center gap-4">
        <motion.button
          onClick={onMenuClick}
          whileTap={{ scale: 0.9 }}
          transition={spring.snappy}
          className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"
          aria-label="Abrir menú"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </motion.button>
      </div>

      <div className="flex items-center gap-3">
        <motion.button
          onClick={openPalette}
          whileTap={{ scale: 0.97 }}
          transition={spring.snappy}
          className="hidden items-center gap-2 rounded-lg border border-border bg-background-secondary px-3 py-1.5 text-[13px] text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:inline-flex cursor-pointer min-w-[200px]"
          aria-label="Abrir paleta de comandos"
          title="Buscar"
          data-tour="admin.header.palette"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 shrink-0"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <span className="flex-1 text-left">Buscar...</span>
          <span className="inline-flex items-center gap-0.5">
            <kbd className="rounded-md border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
              {isMac ? '⌘' : 'Ctrl'}
            </kbd>
            <kbd className="rounded-md border border-border bg-background px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
              K
            </kbd>
          </span>
        </motion.button>

        <motion.button
          onClick={openPalette}
          whileTap={{ scale: 0.9 }}
          transition={spring.snappy}
          className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:hidden"
          aria-label="Buscar"
          data-tour="admin.header.palette"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </motion.button>

        <TourLauncher />

        <NotificationBell />

        <div className="relative flex items-center">
          <motion.button
            ref={menuButtonRef}
            onClick={() => setMenuOpen((o) => !o)}
            whileTap={{ scale: 0.98 }}
            transition={spring.snappy}
            className="flex items-center gap-3 rounded-lg p-1 pl-2 transition-colors duration-200 hover:bg-muted cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="Menú de usuario"
          >
            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium text-foreground leading-tight">
                {userName}
              </p>
              <p className="text-xs text-muted-foreground leading-tight">
                {roleLabel}
              </p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium ring-2 ring-background ring-offset-2 ring-offset-border">
              {getInitials(userName)}
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${menuOpen ? 'rotate-180' : ''}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </motion.button>

          <AnimatePresence>
            {menuOpen && (
              <motion.div
                ref={menuRef}
                initial={{ opacity: 0, scale: 0.96, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{
                  opacity: 0,
                  scale: 0.98,
                  y: -2,
                  transition: { duration: 0.12 },
                }}
                role="menu"
                className="absolute right-0 top-full z-50 mt-2 w-60 overflow-hidden rounded-lg border border-border bg-background"
                style={{
                  transformOrigin: 'top right',
                  boxShadow: 'var(--shadow-elevated)',
                }}
              >
                {/* Identidad (visible también en móvil donde el nombre se oculta) */}
                <div className="border-b border-border px-3 py-2.5 sm:hidden">
                  <p className="text-sm font-medium text-foreground leading-tight">
                    {userName}
                  </p>
                  <p className="text-xs text-muted-foreground leading-tight">
                    {roleLabel}
                  </p>
                </div>

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/account/security');
                  }}
                  className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-foreground transition-colors duration-150 hover:bg-muted cursor-pointer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  Seguridad
                </button>

                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    setShowLogoutConfirm(true);
                  }}
                  className="flex w-full items-center gap-2.5 border-t border-border px-3 py-2.5 text-left text-sm text-error transition-colors duration-150 hover:bg-muted cursor-pointer"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Cerrar sesión
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showLogoutConfirm}
        title="¿Cerrar sesión?"
        message="¿Estás seguro de que deseas cerrar tu sesión?"
        confirmText="Cerrar sesión"
        cancelText="Cancelar"
        confirmVariant="destructive"
        onConfirm={() => {
          setShowLogoutConfirm(false);
          signOut();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </header>
  );
}

export default Header;
